import PropTypes from 'prop-types';
import { useEffect, useRef, useCallback } from 'react';
import authService from '../services/authService';
import { useSelector, useDispatch } from 'react-redux';
import { setCurrentAddress } from '../features/sidePanelSlice';
import { setSidePanelTitle } from '../features/channelSlice';

// Tracks when the last pega-embed was removed so the new one waits for the SDK's async
// disconnectedCallback cleanup to finish before connectedCallback fires on the replacement.
let lastEmbedRemovedAt = 0;
const PEGA_SDK_CLEANUP_DELAY_MS = 600;

export default function PegaWebEmbed(props) {
  const configId = useSelector((state) => state.channel.configId);
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const embedRef = useRef(null);
  const serializedProps = JSON.stringify(props);

  // Extract pyID from full pzInsKey (e.g., "LABB-WORK-SERVICE MA-12345" -> "MA-12345")
  const extractPyID = useCallback((fullKey) => {
    if (!fullKey) return null;
    const key = String(fullKey);
    return key.includes(' ') ? key.split(' ').slice(1).join(' ').toUpperCase() : key.toUpperCase();
  }, []);

  // Handle Pega embed events for navigation tracking
  const handlePegaEvent = useCallback((event) => {
    console.log('📋 PegaWebEmbed event:', event.type, event.detail);
    
    const detail = event.detail || {};
    
    // Handle different event types
    switch (event.type) {
      case 'SdkConstellationReady':
        console.log('🚀 Pega Constellation SDK Ready');
        break;
        
      case 'SdkCaseOpened':
      case 'caseOpened':
        // Case was opened - update address bar
        const caseKey = detail.caseID || detail.pzInsKey || detail.ID;
        if (caseKey) {
          const pyID = extractPyID(caseKey);
          console.log('📂 Case opened:', pyID);
          dispatch(setCurrentAddress(pyID));
          dispatch(setSidePanelTitle(`Case: ${pyID}`));
        }
        break;
        
      case 'SdkAssignmentOpened':
      case 'assignmentOpened':
        // Assignment opened - try to get case ID from it
        const assignmentKey = detail.assignmentID || detail.caseID || detail.ID;
        if (assignmentKey) {
          const pyID = extractPyID(assignmentKey);
          console.log('📋 Assignment opened:', pyID);
          dispatch(setCurrentAddress(pyID));
        }
        break;
        
      case 'SdkViewChanged':
      case 'viewChanged':
        // View changed - could be navigating to home or a different case
        const viewName = detail.viewName || detail.view;
        if (viewName === 'pyHome' || viewName === 'Home' || viewName === 'pyCaseWorkerPortal') {
          console.log('🏠 Navigated to home view');
          dispatch(setCurrentAddress('Home'));
          dispatch(setSidePanelTitle('Employee Panel'));
        }
        break;
        
      case 'SdkCaseClosed':
      case 'caseClosed':
        // Case closed - might return to home
        console.log('📭 Case closed');
        // Don't immediately set to Home - let viewChanged handle it
        break;
        
      default:
        // Log other events for debugging
        if (event.type.startsWith('Sdk') || event.type.includes('case') || event.type.includes('Case')) {
          console.log('📝 Other Pega event:', event.type, detail);
        }
    }
  }, [dispatch, extractPyID]);

  async function loadScript(node) {
    const existingScript = document.querySelectorAll('script[src*="pega-embed.js"]');
    if (existingScript.length > 0) {
      return Promise.resolve(existingScript[0].src.replace('pega-embed.js', ''));
    }
    // resolve script location through the c11nsvc service
    const headers = {
      applicationId: configId,
      hostOnly: true,
      ...authService.getAuthHeader(),
    };
    const response = await fetch(`${process.env.REACT_APP_PEGA_BASE_URL || process.env.REACT_APP_API_BASE_URL}/api/v1/pega/prweb/PRRestService/c11nsvc/v1/config`, {
      headers,
    });
    if (response.status === 200) {
      const staticContentUrl = (await response.json()).staticContentServer;
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `${staticContentUrl}pega-embed.js`;
        script.async = true;
        script.onload = () => resolve(staticContentUrl);
        script.onerror = reject;
        node.appendChild(script);
      });
    }
    return Promise.reject(new Error(await response.text()));
  }

  function insertEmbed(node, embedProps, token, staticContentUrl, followId) {
    if (document.querySelector('pega-embed') || !node) return;
    const embed = document.createElement('pega-embed');

    Object.entries(embedProps).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        embed.setAttribute(key, typeof value === 'object' ?
          JSON.stringify(value) :
          String(value)
        );
      }
    });

    // Quick fix, always default to a full case page for now since that's the main use case and it ensures the header is always visible
    embed.setAttribute('casePage', 'full');
    embed.setAttribute('assignmentHeader', true);

    embed.setAttribute('staticContentUrl', staticContentUrl);
    embed.setAttribute('grantType', 'none');
    embed.setAttribute('authHeader', `Bearer ${token}`);
    embed.setAttribute('additionalHeaders', JSON.stringify({
      applicationId: configId,
      channel: 'employee',
      ...(followId && { 'X-Labb-FollowID': followId }),
    }));
    embed.id = 'theEmbed';

    node.appendChild(embed);
    return embed;
  }

  useEffect(() => {
    if (!containerRef.current) return;
    
    let mounted = true;

    (async () => {
      // Wait for Pega SDK's async disconnectedCallback to finish before inserting
      // a new embed — prevents crash in HybridViewContainer when SDK is mid-cleanup.
      const msAgoRemoved = Date.now() - lastEmbedRemovedAt;
      if (msAgoRemoved < PEGA_SDK_CLEANUP_DELAY_MS) {
        await new Promise(resolve => setTimeout(resolve, PEGA_SDK_CLEANUP_DELAY_MS - msAgoRemoved));
      }

      if (!mounted) return;

      const [staticContentUrl, token] = await Promise.all([
        loadScript(document.body),
        authService.getToken(),
      ]);

      if (!mounted) return;

      embedRef.current = insertEmbed(containerRef.current, props, token, staticContentUrl, authService.getSub());
      
      // Add event listeners for Pega embed events
      if (embedRef.current) {
        const pegaEvents = [
          'SdkConstellationReady',
          'SdkCaseOpened', 
          'SdkAssignmentOpened',
          'SdkViewChanged',
          'SdkCaseClosed',
          'caseOpened',
          'assignmentOpened', 
          'viewChanged',
          'caseClosed'
        ];
        
        pegaEvents.forEach(eventType => {
          embedRef.current.addEventListener(eventType, handlePegaEvent);
        });
      }
    })();
    
    return () => {
      mounted = false;
      lastEmbedRemovedAt = Date.now();

      // Remove event listeners
      if (embedRef.current) {
        const pegaEvents = [
          'SdkConstellationReady',
          'SdkCaseOpened',
          'SdkAssignmentOpened', 
          'SdkViewChanged',
          'SdkCaseClosed',
          'caseOpened',
          'assignmentOpened',
          'viewChanged', 
          'caseClosed'
        ];
        
        pegaEvents.forEach(eventType => {
          embedRef.current?.removeEventListener(eventType, handlePegaEvent);
        });
        
        embedRef.current?.parentNode?.removeChild(embedRef.current);
        embedRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedProps, handlePegaEvent]);

  return <div ref={containerRef} />;
}

PegaWebEmbed.propTypes = {
  style: PropTypes.object,
  id: PropTypes.string,
  // All other props are optional and passed as attributes
};
